import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { apiClient } from '@/lib/api'
import { toast } from '@/hooks/use-toast'
import { useAuth } from '@/contexts/AuthContext'
import type { AirAlert, AirLayer, AirportPoint, AtcFeed, WatchRuleInput } from '@/types/airTraffic'
import AirMap, { type AirFilters, type AirMapHandle, type SelectionOverlay } from './AirMap'
import AlertTicker from './AlertTicker'
import LayerRail from './LayerRail'
import TopBar from './TopBar'
import WatchRulesDialog from './WatchRulesDialog'
import AircraftInspector from './inspector/AircraftInspector'
import AirportInspector from './inspector/AirportInspector'
import AreaInspector from './inspector/AreaInspector'
import OperatorInspector from './inspector/OperatorInspector'
import { AIR, FONT_UI } from './theme'
import useAirLive, { type BBox } from './useAirLive'
import { errorText } from './ui'

type Inspector =
  | { kind: 'aircraft'; hex: string }
  | { kind: 'airport'; icao: string }
  | { kind: 'operator'; name: string }
  | { kind: 'area' }
  | null

const ALL_LAYERS: AirLayer[] = ['mil', 'emg', 'priv', 'area', 'watched']

/** Air Intelligence Console: one live air picture (military, emergencies,
 * privacy-listed and all traffic in view), with aircraft/airport/operator/area
 * inspectors, watch-rule alerts, browser-direct LiveATC audio and PDF reports. */
export default function AirConsole() {
  const { user } = useAuth()
  const mapRef = useRef<AirMapHandle>(null)
  const [layers, setLayers] = useState<Set<AirLayer>>(new Set(ALL_LAYERS))
  const [filters, setFilters] = useState<AirFilters>({ milOnly: false, altBand: 'all', text: '' })
  const [basemap, setBasemap] = useState<'dark' | 'satellite'>('dark')
  const [showAirports, setShowAirports] = useState(true)
  const [airports, setAirports] = useState<AirportPoint[]>([])
  const [view, setView] = useState<{ bbox: BBox; zoom: number } | null>(null)
  const [inspector, setInspector] = useState<Inspector>(null)
  const [selection, setSelection] = useState<SelectionOverlay>({})
  const [alerts, setAlerts] = useState<AirAlert[]>([])
  const [unread, setUnread] = useState(0)
  const [atcFeed, setAtcFeed] = useState<AtcFeed | null>(null)
  const [rulesOpen, setRulesOpen] = useState(false)

  const onAlert = useCallback((a: AirAlert) => {
    setAlerts((prev) => [a, ...prev.filter((x) => x.id !== a.id)].slice(0, 100))
    setUnread((n) => n + 1)
    if (a.severity === 'critical' || a.kind === 'rule') toast({ title: a.title, description: a.detail })
  }, [])
  const live = useAirLive(onAlert)

  const loadAlerts = useCallback(() => {
    apiClient
      .airAlerts(60)
      .then((r) => {
        setAlerts(r.alerts)
        setUnread(r.unread)
      })
      .catch(() => null)
  }, [])
  useEffect(loadAlerts, [loadAlerts])

  const layersRef = useRef(layers)
  layersRef.current = layers
  const onViewChange = useCallback(
    (bbox: BBox, zoom: number) => {
      setView({ bbox, zoom })
      live.setView(bbox, Array.from(layersRef.current))
    },
    [live.setView],
  )

  const toggleLayer = (l: AirLayer) => {
    setLayers((prev) => {
      const next = new Set(prev)
      next.has(l) ? next.delete(l) : next.add(l)
      if (view) live.setView(view.bbox, Array.from(next))
      return next
    })
  }

  // Airports for the view (zoom 5+), debounced by the map's own moveend.
  useEffect(() => {
    if (!view || !showAirports || view.zoom < 5) {
      setAirports([])
      return
    }
    let cancelled = false
    const t = window.setTimeout(() => {
      apiClient
        .airAirports(view.bbox)
        .then((r) => !cancelled && setAirports(r.airports))
        .catch(() => null)
    }, 300)
    return () => {
      cancelled = true
      window.clearTimeout(t)
    }
  }, [view, showAirports])

  const openAircraft = useCallback((hex: string, lon?: number, lat?: number) => {
    if (lon != null && lat != null) mapRef.current?.flyTo(lon, lat)
    setInspector({ kind: 'aircraft', hex: hex.toLowerCase() })
  }, [])
  const openAirport = useCallback((icao: string, lon?: number, lat?: number) => {
    if (lon != null && lat != null) mapRef.current?.flyTo(lon, lat, 8.5)
    setInspector({ kind: 'airport', icao })
  }, [])
  const openOperator = useCallback((name: string) => setInspector({ kind: 'operator', name }), [])
  const closeInspector = useCallback(() => {
    setInspector(null)
    setSelection({})
  }, [])

  const onOpenAlert = (a: AirAlert) => {
    openAircraft(a.hex, a.lon, a.lat)
    if (!a.read) {
      apiClient.airReadAlert(a.id).catch(() => null)
      setAlerts((prev) => prev.map((x) => (x.id === a.id ? { ...x, read: true } : x)))
      setUnread((n) => Math.max(0, n - 1))
    }
  }

  const readAll = () => {
    apiClient.airReadAllAlerts().catch(() => null)
    setAlerts((prev) => prev.map((x) => ({ ...x, read: true })))
    setUnread(0)
  }

  const watch = useCallback(async (rule: WatchRuleInput) => {
    try {
      const r = await apiClient.airCreateWatchRule(rule)
      toast({ title: `Watching: ${r.label}`, description: 'Matches will appear in the alert ticker within seconds.' })
    } catch (e) {
      toast({ title: 'Couldn’t add watch rule', description: errorText(e), variant: 'destructive' })
    }
  }, [])

  const viewCircle = useMemo(() => {
    if (!view) return null
    const [w, s, e, n] = view.bbox
    const lat = (s + n) / 2
    const lon = (w + e) / 2
    return { lat, lon, radius_km: Math.min(500, Math.max(10, Math.round(((n - s) * 111) / 2 * 0.8))) }
  }, [view])

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const inViewAircraft = useMemo(() => Array.from(live.aircraft.current.values()), [live.version])
  const selectedHex = inspector?.kind === 'aircraft' ? inspector.hex : undefined
  const selectedLive = selectedHex ? live.aircraft.current.get(selectedHex) : undefined

  return (
    <div className="flex h-full min-h-0 flex-col" style={{ background: AIR.bg, color: AIR.text, fontFamily: FONT_UI }}>
      <TopBar
        layers={layers}
        onToggleLayer={toggleLayer}
        stats={live.stats}
        health={live.health}
        connected={live.connected}
        lastFrameAt={live.lastFrameAt}
        areaReason={live.area?.reason ?? null}
        watchedCount={live.watched.size}
        onPickAircraft={openAircraft}
        onPickAirport={openAirport}
        onPickOperator={openOperator}
        onOpenArea={() => setInspector({ kind: 'area' })}
        onOpenRules={() => setRulesOpen(true)}
        atcFeed={atcFeed}
        onStopAtc={() => setAtcFeed(null)}
      />
      <div className="flex min-h-0 flex-1">
        <LayerRail
          filters={filters}
          onFilters={setFilters}
          basemap={basemap}
          onBasemap={setBasemap}
          showAirports={showAirports}
          onShowAirports={setShowAirports}
          health={live.health}
          zoom={view?.zoom ?? 0}
        />
        <div className="relative min-w-0 flex-1">
          <AirMap
            ref={mapRef}
            live={live}
            basemap={basemap}
            filters={filters}
            showAirports={showAirports}
            airports={airports}
            selection={{ ...selection, hex: selectedHex }}
            onViewChange={onViewChange}
            onSelectAircraft={openAircraft}
            onSelectAirport={openAirport}
          />
          {!live.connected && (
            <div
              className="pointer-events-none absolute left-1/2 top-3 -translate-x-1/2 rounded-sm px-3 py-1 text-[11px]"
              style={{ background: AIR.panel, color: AIR.muted, boxShadow: `inset 0 0 0 1px ${AIR.lineStrong}` }}
            >
              Connecting to the live feed…
            </div>
          )}
        </div>
        {inspector && (
          <aside className="w-[360px] shrink-0 border-l" style={{ background: AIR.panel, borderColor: AIR.line }}>
            {inspector.kind === 'aircraft' && (
              <AircraftInspector
                key={inspector.hex}
                hex={inspector.hex}
                live={selectedLive}
                watched={live.watched}
                onClose={closeInspector}
                onSelection={setSelection}
                onWatch={watch}
                onOpenOperator={openOperator}
                onOpenAirport={openAirport}
              />
            )}
            {inspector.kind === 'airport' && (
              <AirportInspector
                key={inspector.icao}
                icao={inspector.icao}
                watched={live.watched}
                playing={atcFeed}
                onPlay={setAtcFeed}
                onClose={closeInspector}
                onSelection={setSelection}
                onOpenAircraft={openAircraft}
                onWatch={watch}
              />
            )}
            {inspector.kind === 'operator' && (
              <OperatorInspector key={inspector.name} name={inspector.name} watched={live.watched} onClose={closeInspector} onOpenAircraft={openAircraft} onWatch={watch} />
            )}
            {inspector.kind === 'area' && (
              <AreaInspector
                bbox={view?.bbox ?? null}
                aircraft={inViewAircraft}
                watched={live.watched}
                areaOn={!!live.area?.on}
                onClose={closeInspector}
                onSelection={setSelection}
                onOpenAircraft={openAircraft}
                onOpenOperator={openOperator}
                onWatch={watch}
              />
            )}
          </aside>
        )}
      </div>
      <AlertTicker alerts={alerts} unread={unread} onOpen={onOpenAlert} onReadAll={readAll} />
      <WatchRulesDialog
        open={rulesOpen}
        onClose={() => setRulesOpen(false)}
        isAdmin={user?.role === 'admin'}
        viewCircle={viewCircle}
        onChanged={() => null}
      />
    </div>
  )
}
